import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssociationWizard } from '../AssociationWizard';
import {
  BillingToggleSection,
  buildBillingLabels
} from '../shared/BillingToggleSection';
import { ConfirmAndRunStep, SummaryRow } from '../shared/ConfirmAndRunStep';
import { RuleViolation } from '../../helpers/types';
import {
  CredentialsStep,
  CredentialsFormValues,
  isCredentialsValid,
  trimCredentials,
  buildCredentialsLabels
} from '../shared/CredentialsStep';

const WIZARD = 'tenantsAssociation.mspChildWizard';
const CONFIRM = `${WIZARD}.confirmStep`;
const CREDENTIALS = `${WIZARD}.credentialsStep`;

const ALL_CHILD_ACTIONS = [
  'ACTION_CLOUDINFRA_AUTH',
  'ACTION_CREATE_CHILD',
  'ACTION_ASSIGN_PAYG',
  'ACTION_ASSOCIATE_SASE',
  'ACTION_UPDATE_TYPE',
  'ACTION_UPDATE_SUBSCRIPTION'
] as const;

export interface MspChildAssociationPayload {
  credentials: CredentialsFormValues;
}

interface MspChildWizardProps {
  /** Upfront validation failures from the last rejected attempt. */
  upfrontErrors?: RuleViolation[];
  credentials: CredentialsFormValues;
  onCredentialsChange: (next: CredentialsFormValues) => void;
  scheduledConfirmed: boolean;
  onScheduledConfirmedChange: (next: boolean) => void;
  onBack: () => void;
  onFinish: (payload: MspChildAssociationPayload) => void;
  isFinishing?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const MspChildWizard = ({
  upfrontErrors,
  credentials,
  onCredentialsChange,
  scheduledConfirmed,
  onScheduledConfirmedChange,
  onBack,
  onFinish,
  isFinishing,
  onStepChange
}: MspChildWizardProps) => {
  const { t } = useTranslation('activity');

  const handleFinish = useCallback(
    () =>
      onFinish({
        credentials: trimCredentials(credentials)
      }),
    [onFinish, credentials]
  );

  const summaryRows = useMemo<SummaryRow[]>(
    () => [
      {
        label: t(`${CONFIRM}.SUMMARY_MODE`),
        value: t(`${CONFIRM}.MODE_VALUE`)
      },
      {
        label: t(`${CONFIRM}.SUMMARY_PARENT_UUID`),
        value: credentials.parentUuid
      },
      {
        label: t(`${CONFIRM}.SUMMARY_ADMIN_EMAIL`),
        value: credentials.adminEmail
      },
      {
        label: t(`${CONFIRM}.SUMMARY_BILLING`),
        value: t(`${CONFIRM}.BILLING_MOVE_PAYG`)
      }
    ],
    [t, credentials.parentUuid, credentials.adminEmail]
  );

  const actions = useMemo(
    () => ALL_CHILD_ACTIONS.map(key => t(`${CONFIRM}.${key}`)),
    [t]
  );

  const steps = useMemo(
    () => [
      {
        content: (
          <CredentialsStep
            values={credentials}
            onChange={onCredentialsChange}
            labels={buildCredentialsLabels(t, WIZARD)}
          >
            <BillingToggleSection labels={buildBillingLabels(t, WIZARD)} />
          </CredentialsStep>
        ),
        isValid: isCredentialsValid(credentials),
        subtitle: t(`${CREDENTIALS}.PAGE_SUBTITLE`)
      },
      {
        content: (
          <ConfirmAndRunStep
            scheduledConfirmed={scheduledConfirmed}
            onConfirmChange={onScheduledConfirmedChange}
            summaryRows={summaryRows}
            actions={actions}
            actionsTitle={t(`${CONFIRM}.ACTIONS_TITLE`)}
            confirmCheckboxLabel={t(`${CONFIRM}.CONFIRMATION_CHECKBOX`)}
            upfrontErrors={upfrontErrors}
            upfrontErrorsTitle={t(`${CONFIRM}.VALIDATION_FAILED_TITLE`)}
          />
        ),
        isValid: scheduledConfirmed,
        subtitle: t(`${CONFIRM}.PAGE_SUBTITLE`)
      }
    ],
    [
      credentials,
      onCredentialsChange,
      scheduledConfirmed,
      onScheduledConfirmedChange,
      summaryRows,
      actions,
      upfrontErrors,
      t
    ]
  );

  return (
    <AssociationWizard
      steps={steps}
      cancelLabel={t('buttons.CANCEL')}
      nextLabel={t(`${WIZARD}.NEXT`)}
      previousLabel={t(`${WIZARD}.PREVIOUS`)}
      finishLabel={t(`${WIZARD}.RUN_ASSOCIATION`)}
      onBack={onBack}
      onFinish={handleFinish}
      isFinishing={isFinishing}
      onStepChange={onStepChange}
    />
  );
};

export default MspChildWizard;
